import {
    AutocompleteOption,
    AutocompleteOptionsProps,
} from "components/Form/components/Autocomplete/Autocomplete";
import { getBundles } from "containers/Bundles/Bundles.selectors";
import { getProjects } from "containers/Projects/Projects.selectors";
import { isEqual } from "lodash";
import { useMemo } from "react";
import { useSelector } from "react-redux";

// Roles are kept live in the shared DataStore slice by the /ui websocket
// (role_plain_list -> STORE_DATA key "rolesAutocomplete"). The selector is
// inlined so this hook does not import the RoleSelect component.
const rolesAutocompleteSelector = (state: any): any[] =>
    state?.dataStore?.rolesAutocomplete ?? [];

// Reference-picker resources whose FULL list is already kept live in Redux by
// the /ui websocket subscription (project_plain_list / bundle_plain_list /
// role_plain_list; see MultiWebsocket). Serving their pickers from the store
// eliminates the per-open GraphQL refetch AND the initial single-record label
// lookup, with no staleness cost: a create / rename / delete in any session is
// pushed to the slice live. This is the key difference from the fetch-saga-fed
// generic Autocomplete slice, whose contents can go stale if the refetch is
// skipped.
const LIVE_LIST_RESOURCES = new Set(["project", "bundle", "role"]);

/**
 * When a reference picker targets a resource whose full list is already kept
 * live in Redux, return an options object with a static `list` (built from the
 * live slice) and `resource` stripped, so the generic Autocomplete renders it
 * as static options (no fetch-on-open, no infinite loader, no initial single-id
 * lookup). Every other picker is returned unchanged, keeping the generic
 * GraphQL async path intact.
 */
export const useCachedResourceOptions = (
    options: AutocompleteOptionsProps
): AutocompleteOptionsProps => {
    // Hooks must run unconditionally; the slices are cheap and change rarely.
    const projects = useSelector(getProjects, isEqual);
    const bundles = useSelector(getBundles, isEqual);
    const roles = useSelector(rolesAutocompleteSelector, isEqual);

    return useMemo(() => {
        const { resource } = options;
        // Only intervene for the known live-cached resources.
        if (!resource || !LIVE_LIST_RESOURCES.has(resource)) {
            return options;
        }
        // Respect an explicit static configuration if one is already provided.
        if (
            options.list !== undefined ||
            options.group !== undefined ||
            options.enumName
        ) {
            return options;
        }

        const source =
            resource === "project"
                ? projects
                : resource === "bundle"
                  ? bundles
                  : roles;
        if (!Array.isArray(source)) {
            return options;
        }

        let list: AutocompleteOption[] = source.map((item: any) => ({
            ...item,
            value: options.hasNameAsOptionValue ? item.name : item.id,
            label: item.name,
        }));
        if (options.filterOptions) {
            list = list.filter(options.filterOptions);
        }

        // Strip `resource` so downstream treats these as static options.
        const { resource: _omittedResource, ...rest } = options;
        return { ...rest, list };
    }, [options, projects, bundles, roles]);
};
